import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, FileText, Trash2, Plus, X, RefreshCcw, Calculator, Globe } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "CNY", "JPY", "AUD", "CAD"];
const SOURCING_MODELS = ["buying_agent", "principal"];

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Input = (props) => (
  <input {...props} className={"w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 " + (props.className || "")} />
);

const CellInput = (props) => (
  <input {...props} className={"w-full border border-transparent hover:border-zinc-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-zinc-950 " + (props.className || "")} />
);

export default function CostSheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cs, setCs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [calculatingLanded, setCalculatingLanded] = useState(false);
  const [legalEntities, setLegalEntities] = useState([]);

  useEffect(() => {
    api.get(`/costs/${id}`).then((r) => setCs(r.data)).catch(() => navigate("/costing"));
    // A swallowed failure here is indistinguishable from an empty entity master — both render
    // two blank dropdowns with no explanation.
    api.get("/legal-entities").then((r) => setLegalEntities(r.data))
      .catch((err) => console.error("Failed to load legal entities:", err));
  }, [id, navigate]);

  if (!cs) return <div className="p-10 text-sm text-zinc-400">Loading…</div>;
  const canEdit = user?.role === "admin" || user?.id === cs.owner_id;
  const cur = cs.currency || "USD";

  // Local totals (mirror backend calculation)
  const materials_total = cs.materials.reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0);
  const labor_total = cs.labor.reduce((s, r) => s + (r.cost || 0), 0);
  const overheads_total = cs.overheads.reduce((s, r) => s + (r.cost || 0), 0);
  const logistics_total = cs.logistics.reduce((s, r) => s + (r.cost || 0), 0);
  const landed = materials_total + labor_total + overheads_total + logistics_total;
  const wholesale = landed * (1 + (cs.margin_percent || 0) / 100);
  const retail = wholesale * (cs.retail_multiplier || 1);

  const set = (patch) => setCs({ ...cs, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: cs.currency, materials: cs.materials, labor: cs.labor,
        overheads: cs.overheads, logistics: cs.logistics,
        margin_percent: parseFloat(cs.margin_percent) || 0,
        retail_multiplier: parseFloat(cs.retail_multiplier) || 1,
        notes: cs.notes,
        sourcing_model: cs.sourcing_model || null,
        origin_country: cs.origin_country || null,
        destination_country: cs.destination_country || null,
        contracting_entity_id: cs.contracting_entity_id || null,
        importing_entity_id: cs.importing_entity_id || null,
      };
      const { data } = await api.patch(`/costs/${id}`, payload);
      setCs(data);
      toast.success("Cost sheet saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/costs/${id}/export/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cs.style_number || "cost-sheet"}-cost.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const syncBom = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/costs/${id}/sync-bom`);
      setCs(data.cost_sheet);
      toast.success(data.added ? `Added ${data.added} new material row${data.added !== 1 ? "s" : ""}` : "BOM already in sync");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const calculateLanded = async () => {
    setCalculatingLanded(true);
    try {
      const { data } = await api.post(`/costs/${id}/calculate-landed`);
      setCs(data);
      toast.success("Landed cost calculated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalculatingLanded(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this cost sheet?")) return;
    await api.delete(`/costs/${id}`);
    navigate("/costing");
  };

  const updateMaterial = (i, patch) => {
    const materials = [...cs.materials];
    materials[i] = { ...materials[i], ...patch };
    set({ materials });
  };
  const removeMaterial = (i) => set({ materials: cs.materials.filter((_, x) => x !== i) });
  const addMaterial = () => set({ materials: [...cs.materials, { id: crypto.randomUUID(), placement: "", product: "", composition: "", quantity: 0, unit: "m", unit_cost: 0, notes: "" }] });

  const updateGeneric = (key, i, patch) => {
    const arr = [...cs[key]];
    arr[i] = { ...arr[i], ...patch };
    set({ [key]: arr });
  };
  const removeGeneric = (key, i) => set({ [key]: cs[key].filter((_, x) => x !== i) });
  const addGeneric = (key, name) => set({ [key]: [...cs[key], { id: crypto.randomUUID(), name: name || "New item", cost: 0, notes: "" }] });

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button data-testid="costsheet-back" onClick={() => navigate("/costing")} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} /> Costing
      </button>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Cost Sheet · {cs.style_number}</p>
          <h1 className="font-heading text-3xl font-bold mt-1 tracking-tight truncate" data-testid="costsheet-title">{cs.style_name}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            By {cs.owner_name} · updated {new Date(cs.updated_at || cs.created_at).toLocaleDateString()}
            {" · "}<Link to={`/techpacks/${cs.techpack_id}`} className="underline hover:text-zinc-950">Open techpack</Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="costsheet-save" onClick={save} disabled={!canEdit || saving}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="costsheet-export" onClick={exportPdf} disabled={exporting}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-40">
            <FileText size={14} /> {exporting ? "Exporting…" : "Export PDF"}
          </button>
          {canEdit && (
            <button data-testid="costsheet-delete" onClick={del} className="p-2.5 border border-zinc-300 rounded-md hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          {cs.cover_image && <img src={imgUrl(cs.cover_image)} alt={cs.style_name} className="w-full rounded-lg border border-zinc-200 bg-white object-contain" />}
          {cs.product_description && (
            <div className="border border-zinc-200 bg-white rounded-lg p-4" data-testid="costsheet-product-description">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Marketing copy</p>
              {cs.product_title && <p className="text-sm font-heading font-bold" data-testid="costsheet-product-title">{cs.product_title}</p>}
              <p className={"text-xs text-zinc-700 whitespace-pre-line leading-relaxed " + (cs.product_title ? "mt-2" : "")}>{cs.product_description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Currency">
              <select data-testid="costsheet-currency" value={cs.currency} onChange={(e) => set({ currency: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Margin %">
              <Input data-testid="costsheet-margin" type="number" step="0.5" value={cs.margin_percent} onChange={(e) => set({ margin_percent: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Retail multiplier">
              <Input data-testid="costsheet-multiplier" type="number" step="0.1" value={cs.retail_multiplier} onChange={(e) => set({ retail_multiplier: parseFloat(e.target.value) || 1 })} />
            </Field>
          </div>

          {/* Customs & Entities */}
          <div className="border border-zinc-200 bg-white rounded-lg p-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-medium flex items-center gap-1.5"><Globe size={12} /> Customs & Entities</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sourcing model">
                <select data-testid="costsheet-sourcing-model" value={cs.sourcing_model || ""} onChange={(e) => set({ sourcing_model: e.target.value || null })}
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select...</option>
                  {SOURCING_MODELS.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Origin country">
                <Input data-testid="costsheet-origin-country" value={cs.origin_country || ""} onChange={(e) => set({ origin_country: e.target.value })} placeholder="e.g. CN" />
              </Field>
              <Field label="Destination country">
                <Input data-testid="costsheet-destination-country" value={cs.destination_country || ""} onChange={(e) => set({ destination_country: e.target.value })} placeholder="e.g. GB" />
              </Field>
              <Field label="Contracting entity">
                <select data-testid="costsheet-contracting-entity" value={cs.contracting_entity_id || ""} onChange={(e) => set({ contracting_entity_id: e.target.value || null })}
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select...</option>
                  {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
                </select>
              </Field>
              <Field label="Importing entity">
                <select data-testid="costsheet-importing-entity" value={cs.importing_entity_id || ""} onChange={(e) => set({ importing_entity_id: e.target.value || null })}
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select...</option>
                  {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
                </select>
              </Field>
            </div>
            <button data-testid="costsheet-calculate-landed" onClick={calculateLanded} disabled={calculatingLanded}
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
              <Calculator size={14} /> {calculatingLanded ? "Calculating..." : "Calculate Landed Cost"}
            </button>
          </div>

          <div className="border border-zinc-950 bg-zinc-950 text-white rounded-lg p-4 space-y-3" data-testid="costsheet-totals">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Per unit</p>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Materials</p>
              <p className="text-sm">{cur} {materials_total.toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Making</p>
              <p className="text-sm">{cur} {labor_total.toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Overheads</p>
              <p className="text-sm">{cur} {overheads_total.toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Shipping</p>
              <p className="text-sm">{cur} {logistics_total.toFixed(2)}</p>
            </div>
            {cs.customs_value != null && (
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-zinc-400">Customs Value</p>
                <p className="text-sm">{cur} {parseFloat(cs.customs_value).toFixed(2)}</p>
              </div>
            )}
            {cs.duty_amount != null && (
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-zinc-400">Duty Amount</p>
                <p className="text-sm">{cur} {parseFloat(cs.duty_amount).toFixed(2)}</p>
              </div>
            )}
            {cs.import_vat != null && (
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-zinc-400">Import VAT</p>
                <p className="text-sm">{cur} {parseFloat(cs.import_vat).toFixed(2)}</p>
              </div>
            )}
            {cs.sourcing_model === "buying_agent" && (
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-zinc-400">Intercompany Margin (5%)</p>
                <p className="text-sm">{cur} {((materials_total + labor_total + overheads_total) * 0.05).toFixed(2)}</p>
              </div>
            )}
            <div className="border-t border-zinc-700 pt-3 flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider">Landed</p>
              <p className="text-lg font-bold" data-testid="costsheet-landed">{cur} {landed.toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Wholesale</p>
              <p className="text-sm font-medium" data-testid="costsheet-wholesale">{cur} {wholesale.toFixed(2)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-zinc-400">Retail</p>
              <p className="text-sm font-medium" data-testid="costsheet-retail">{cur} {retail.toFixed(2)}</p>
            </div>
          </div>

          {/* Margin Analysis */}
          <div className="border border-zinc-200 bg-white rounded-lg p-4 space-y-3" data-testid="costsheet-margin-analysis">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-medium">Margin Analysis</p>
            {/* Cost breakdown bar */}
            {landed > 0 && (
              <div>
                <div className="flex h-4 rounded-full overflow-hidden">
                  <div className="bg-blue-500" style={{ width: `${(materials_total / landed) * 100}%` }} title={`Materials ${((materials_total / landed) * 100).toFixed(0)}%`} />
                  <div className="bg-amber-500" style={{ width: `${(labor_total / landed) * 100}%` }} title={`Making ${((labor_total / landed) * 100).toFixed(0)}%`} />
                  <div className="bg-purple-500" style={{ width: `${(overheads_total / landed) * 100}%` }} title={`Overheads ${((overheads_total / landed) * 100).toFixed(0)}%`} />
                  <div className="bg-emerald-500" style={{ width: `${(logistics_total / landed) * 100}%` }} title={`Shipping ${((logistics_total / landed) * 100).toFixed(0)}%`} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-blue-600">Materials {((materials_total / landed) * 100).toFixed(0)}%</span>
                  <span className="text-[9px] text-amber-600">Making {((labor_total / landed) * 100).toFixed(0)}%</span>
                  <span className="text-[9px] text-purple-600">OH {((overheads_total / landed) * 100).toFixed(0)}%</span>
                  <span className="text-[9px] text-emerald-600">Ship {((logistics_total / landed) * 100).toFixed(0)}%</span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-zinc-50 rounded p-2.5">
                <p className="text-[10px] text-zinc-400">Gross Profit</p>
                <p className="text-sm font-bold">{cur} {(wholesale - landed).toFixed(2)}</p>
              </div>
              <div className="bg-zinc-50 rounded p-2.5">
                <p className="text-[10px] text-zinc-400">Gross Margin %</p>
                <p className="text-sm font-bold">{wholesale > 0 ? (((wholesale - landed) / wholesale) * 100).toFixed(1) : "0.0"}%</p>
              </div>
              <div className="bg-zinc-50 rounded p-2.5">
                <p className="text-[10px] text-zinc-400">Retail Margin</p>
                <p className="text-sm font-bold">{cur} {(retail - wholesale).toFixed(2)}</p>
              </div>
              <div className="bg-zinc-50 rounded p-2.5">
                <p className="text-[10px] text-zinc-400">Retail Margin %</p>
                <p className="text-sm font-bold">{retail > 0 ? (((retail - wholesale) / retail) * 100).toFixed(1) : "0.0"}%</p>
              </div>
            </div>
            {/* Duty rate info if available */}
            {cs.duty_rate != null && (
              <div className="bg-zinc-50 rounded p-2.5">
                <p className="text-[10px] text-zinc-400">Applicable Duty Rate</p>
                <p className="text-sm font-bold">{cs.duty_rate}%{cs.hs_code ? ` (HS: ${cs.hs_code})` : ""}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <section data-testid="section-materials">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="font-heading text-lg font-bold">Materials</h2>
                <p className="text-xs text-zinc-500">Populated from the techpack BOM. Enter quantity and unit cost per material.</p>
              </div>
              <button data-testid="costsheet-sync-bom" onClick={syncBom} disabled={syncing}
                title="Pull any new BOM rows from the techpack"
                className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 disabled:opacity-40">
                <RefreshCcw size={12} /> {syncing ? "Syncing…" : "Sync BOM"}
              </button>
            </div>
            <div className="overflow-x-auto border border-zinc-200 rounded-lg bg-white">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-950 text-white">
                    <th className="text-left px-2 py-2">Placement</th>
                    <th className="text-left px-2 py-2">Product</th>
                    <th className="text-left px-2 py-2">Composition</th>
                    <th className="text-center px-2 py-2 w-16">Qty</th>
                    <th className="text-center px-2 py-2 w-14">Unit</th>
                    <th className="text-right px-2 py-2 w-20">Unit {cur}</th>
                    <th className="text-right px-2 py-2 w-20">Total {cur}</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {cs.materials.length === 0 && (
                    <tr><td colSpan="8" className="text-center text-zinc-400 py-6 text-xs">No materials. Add rows or sync from techpack BOM.</td></tr>
                  )}
                  {cs.materials.map((m, i) => (
                    <tr key={m.id} className="border-t border-zinc-100" data-testid={`material-row-${i}`}>
                      <td className="px-1"><CellInput value={m.placement} onChange={(e) => updateMaterial(i, { placement: e.target.value })} /></td>
                      <td className="px-1"><CellInput value={m.product} onChange={(e) => updateMaterial(i, { product: e.target.value })} /></td>
                      <td className="px-1"><CellInput value={m.composition} onChange={(e) => updateMaterial(i, { composition: e.target.value })} /></td>
                      <td className="px-1"><CellInput type="number" step="0.01" className="text-center" data-testid={`material-qty-${i}`} value={m.quantity} onChange={(e) => updateMaterial(i, { quantity: parseFloat(e.target.value) || 0 })} /></td>
                      <td className="px-1"><CellInput className="text-center" value={m.unit} onChange={(e) => updateMaterial(i, { unit: e.target.value })} /></td>
                      <td className="px-1"><CellInput type="number" step="0.01" className="text-right" data-testid={`material-unit-cost-${i}`} value={m.unit_cost} onChange={(e) => updateMaterial(i, { unit_cost: parseFloat(e.target.value) || 0 })} /></td>
                      <td className="px-1 text-right font-medium">{((m.quantity || 0) * (m.unit_cost || 0)).toFixed(2)}</td>
                      <td className="px-1"><button onClick={() => removeMaterial(i)} data-testid={`material-remove-${i}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button></td>
                    </tr>
                  ))}
                  <tr className="border-t border-zinc-200 bg-zinc-50 font-medium">
                    <td colSpan="6" className="px-2 py-2 text-right">Materials subtotal</td>
                    <td className="px-2 py-2 text-right">{materials_total.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button data-testid="material-add" onClick={addMaterial} className="mt-2 inline-flex items-center gap-2 text-xs border border-dashed border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100"><Plus size={12} /> Add material</button>
          </section>

          {[
            ["labor", "Cost of Making", "Labor operations that make the garment (cutting, sewing, finishing).", labor_total],
            ["overheads", "Overheads & Miscellaneous", "Trims, packaging, factory overhead and misc costs.", overheads_total],
            ["logistics", "Shipping & Logistics", "Freight, duty, insurance and delivery.", logistics_total],
          ].map(([key, title, hint, subtotal]) => (
            <section key={key} data-testid={`section-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="font-heading text-lg font-bold">{title}</h2>
                  <p className="text-xs text-zinc-500">{hint}</p>
                </div>
              </div>
              <div className="overflow-x-auto border border-zinc-200 rounded-lg bg-white">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-950 text-white">
                      <th className="text-left px-2 py-2">Item</th>
                      <th className="text-right px-2 py-2 w-24">Cost {cur}</th>
                      <th className="text-left px-2 py-2">Notes</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cs[key].map((r, i) => (
                      <tr key={r.id} className="border-t border-zinc-100" data-testid={`${key}-row-${i}`}>
                        <td className="px-1"><CellInput value={r.name} onChange={(e) => updateGeneric(key, i, { name: e.target.value })} /></td>
                        <td className="px-1"><CellInput type="number" step="0.01" className="text-right" data-testid={`${key}-cost-${i}`} value={r.cost} onChange={(e) => updateGeneric(key, i, { cost: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="px-1"><CellInput value={r.notes} onChange={(e) => updateGeneric(key, i, { notes: e.target.value })} /></td>
                        <td className="px-1"><button onClick={() => removeGeneric(key, i)} data-testid={`${key}-remove-${i}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button></td>
                      </tr>
                    ))}
                    <tr className="border-t border-zinc-200 bg-zinc-50 font-medium">
                      <td className="px-2 py-2 text-right">{title} subtotal</td>
                      <td className="px-2 py-2 text-right">{subtotal.toFixed(2)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button data-testid={`${key}-add`} onClick={() => addGeneric(key)} className="mt-2 inline-flex items-center gap-2 text-xs border border-dashed border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100"><Plus size={12} /> Add row</button>
            </section>
          ))}

          <section>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Notes</label>
            <textarea data-testid="costsheet-notes" rows={3} value={cs.notes || ""} onChange={(e) => set({ notes: e.target.value })}
              placeholder="Assumptions, negotiation notes, min order quantity, etc."
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </section>
        </div>
      </div>
    </div>
  );
}
